import { animate, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderHeader } from '@app/core/models/order-header.model';
import { AppLocaleService } from '@app/core/services/app-locale.service';
import { OrdersService } from '@app/core/services/orders.service';
import { CassError } from '@core/models/cass-error';
import { CustomerContact } from '@core/models/customer-contact.model';
import { Customer } from '@core/models/customer.model';
import { DbConfig } from '@core/models/db-config';
import { Package } from '@core/models/package.model';
import { UserProfile } from '@core/models/user-profile';
import { AppConfigService } from '@core/services/app-config.service';
import { CustomerService } from '@core/services/customer.service';
import { Logger } from '@core/services/logger.service';
import { UserProfilesService } from '@core/services/user-profiles.service';
import { OrderHeaderConverterService } from '@order/services/order-header.converter';
import { StaticDataCacheService } from '@order/services/static-data-cache.service';
import { DateTimeService } from '@shared/services/date-time.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { CustomerContactViewModel } from '@shared/viewModels/customer-contact-view-model';
import { OrderHeaderViewModel } from '@shared/viewModels/order-header.viewModel';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { SubSink } from 'subsink';

type DisplayMode = "showAll" | "showRecent" | "externalPlanner" | undefined;

@Component({
	selector: 'cass-my-ads-list',
	templateUrl: './my-ads-list.component.html',
	styleUrls: ['./my-ads-list.component.css'],
	animations: [
		trigger(
			'expandCollapseAnimationContent', [
				transition(
					':enter', [
						style({ height: '0' }),
						animate('0.5s ease', style({ height: '*' }))
					]
				)
			]
		)
	],
})
export class MyAdsListComponent implements OnInit, OnDestroy {

	private readonly COMPONENT_NAME = "MyAdsListComponent";

	private readonly MAX_PAGES = 1000;

	private readonly ORDER_PAGESIZE = 5;
	private readonly RECENT_ITEMS = 10;

	@Input()
	showToolsPanel = false;

	@Input("expand")
	get forceExpand() { return this._forceExpand };
	set forceExpand(value: any) { this._forceExpand = (value != null && `${value}` !== 'false') };
	private _forceExpand: boolean = false;

	@Input()
	set displayMode(mode: DisplayMode) {
		this._displayMode$.next(mode);
	}
	get displayMode(): DisplayMode {
		return this._displayMode$.value;
	}
	private readonly _displayMode$ = new BehaviorSubject<DisplayMode>("showRecent");

	searchControl = new FormControl("");
	toolsForm = new FormGroup({
		searchControl: this.searchControl,
	});

	orderHeadersVMs: OrderHeaderViewModel[] = [];
	refinedOrderHeadersVMs: OrderHeaderViewModel[] = [];
	expandedItemIdx?: Number;

	readonly callingOut$ = new BehaviorSubject<boolean>(false);
	readonly filterDataReady$ = new BehaviorSubject<boolean>(false);
	filterPackagesReady: boolean = false;

	private orderHeadersRequestedForEmail: string | undefined;
	private orderHeadersRequestedForDisplayMode: DisplayMode;
	private orderHeadersRequestedForPolySearch: string | undefined = "";
	private _agency: Customer | undefined;

	private advertiserId: string | undefined;
	private agencyId: string | undefined;
	private getOrderHeadersSubscribe: Subscription | undefined;

	isFilterPanelActive: boolean = false;

	packageLabels: string[] = [];
	contacts: CustomerContactViewModel[] = [];

	// This array contains the translation keys of each status filter.
	// The filter code also uses the key to switch filter logic.
	readonly statuses: string[] = [
		"ORDER_BASKET.STATUS.DRAFT",
		"ORDER_BASKET.STATUS.RESERVED",
		"ORDER_BASKET.STATUS.APPROVAL_PENDING",
		"ORDER_BASKET.STATUS.REJECTED",
		"ORDER_BASKET.STATUS.WAITING_MATERIAL",
		"ORDER_BASKET.STATUS.CONFIRMED",
		"ORDER_BASKET.STATUS.STARTED",
		"ORDER_BASKET.STATUS.FINISHED",
		"ORDER_BASKET.STATUS.CANCELLED"
	]

	productFilterValue: string = "";
	contactIdFilterValue: string = "";
	statusFilterValue: string = "";
	issueDateFromFilterValue: Date | undefined = undefined;
	issueDateFromFilterString: string = "";
	issueDateToFilterValue: Date | undefined = undefined;
	issueDateToFilterString: string = "";
	showCustomerAndAgency: boolean = false;

	private readonly _ordersLoadingComplete$ = new BehaviorSubject<boolean>(false);
	displayNoAdsAlert: boolean = false;

	private subs = new SubSink();

	constructor (
		private _activatedRoute: ActivatedRoute,
		private _alertService: UserAlertService,
		private _appConfigService: AppConfigService,
		private _dateTimeService: DateTimeService,
		private _logger: Logger,
		private _orderHeaderConverterService: OrderHeaderConverterService,
		private _orderService: OrdersService,
		private _router: Router,
		private _staticDataCacheService: StaticDataCacheService,
		private _userProfilesService: UserProfilesService,
		private _customerService: CustomerService,
		public localeService: AppLocaleService,
	) { }

	ngOnInit(): void {

		this.advertiserId = this._activatedRoute.snapshot.queryParamMap.get("advertiserId") || undefined;
		this.agencyId = this._activatedRoute.snapshot.queryParamMap.get("agencyId") || undefined;

		this.subs.add(
			combineLatest([this._userProfilesService.userProfile$, this._displayMode$, this._appConfigService.dbConfig$]).subscribe(([profile, displayMode, dbConfig]: [UserProfile | undefined, DisplayMode, DbConfig]) => {
				this.updateOrderHeaders(profile, displayMode, this.advertiserId, this.agencyId);
				this.initFilterData();
			}));

		this.subs.add(
			this._userProfilesService.userProfile$.subscribe({
				next: (profile) => {
					profile && this.updateCustomerAndAgencyVisibility(profile);
					if (profile?.customers?.[0] != undefined) {
						this._agency = profile.customers[0];
					};
				},
				error: (error: CassError) => {
					this._logger.error(this.COMPONENT_NAME, "Init failed: cannot get user profile or agency's customers");
					this._alertService.errorByCassError(error);
				}
			}));

		this.subs.add(
			this._ordersLoadingComplete$.subscribe((loadingComplete: boolean) => {
				if (loadingComplete && this.refinedOrderHeadersVMs.length === 0) {
					this.displayNoAdsAlert = true;
				}
			})
		);
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	private initFilterData(): void {
		// make sure to call this method after the this._userProfilesService.userProfile$ is initialized, as the method accesses it directly
		const profile = this._userProfilesService.userProfile$.value;
		if (profile && profile.customers && profile.customers.length > 0) {
			profile.customers.forEach((customer: Customer) => {
				if (customer && customer.contacts && customer.contacts.length > 0) {
					customer.contacts.forEach((contact: CustomerContact) => this.contacts.push(new CustomerContactViewModel(contact)));
				}
			});
		}

		this.contacts.sort((a, b) => (a.displayName > b.displayName ? 1 : b.displayName > a.displayName ? -1 : 0)); // sort by displayName

		// set filterDataReady$ before the packages are retrieved to let users use the filter panel even if the package retrieval is in progress or failed
		this.filterDataReady$.next(true);

		this.subs.add(
			this._staticDataCacheService.init().subscribe({
				next: () => {
					this.packageLabels = this._staticDataCacheService.packages.map((pckg: Package) => pckg.label);
					this.filterPackagesReady = true;
				}
			}));
	}

	private updateOrderHeaders(profile: UserProfile | undefined, displayMode: DisplayMode, filterByAdvertiserId: string | undefined, filterByAgencyId: string | undefined): void {

		if (!profile) {
			return;
		}

		this.displayNoAdsAlert = false;

		const polySearch = this.searchControl.value as string | undefined;

		if (this.orderHeadersRequestedForEmail === profile.email && this.orderHeadersRequestedForDisplayMode === displayMode && this.orderHeadersRequestedForPolySearch === polySearch) {
			this._ordersLoadingComplete$.next(true);
			return;
		}

		this.resetOrderHeaderVMs(); // reset view models here - only when FE is going to request another batch using different criteria

		this.orderHeadersRequestedForEmail = profile.email;
		this.orderHeadersRequestedForDisplayMode = displayMode;
		this.orderHeadersRequestedForPolySearch = polySearch;

		const startDate: Date = new Date("2000-01-01T00:00:00Z");

		let advertiserId = filterByAdvertiserId;
		let agencyId = filterByAgencyId;

		// TODO surely makes more sense to pass in the page size depending on the showAll mode?
		let pageSize = this.ORDER_PAGESIZE;
		let pageOffset = 1;
		this.loadOrderHeaders(startDate, advertiserId, agencyId, displayMode, profile, pageOffset, pageSize, polySearch);
	}

	private loadOrderHeaders(startDate: Date,
		advertiserId: string | undefined,
		otherPayerId: string | undefined,
		displayMode: DisplayMode,
		profile: UserProfile | undefined,
		pageOffset: number,
		pageSize: number,
		polySearch: string | undefined) {

		if (pageOffset > this.MAX_PAGES) {
			return;
		}

		this.callingOut$.next(true);

		this.getOrderHeadersSubscribe = this._orderService.getOrderHeaders(startDate, advertiserId, otherPayerId, pageOffset++, pageSize, polySearch).subscribe({
			next: (orderHeaders: OrderHeader[]) => {
				this.callingOut$.next(false);

				if (!orderHeaders || orderHeaders.length === 0) {
					this._ordersLoadingComplete$.next(true);
					return;
				}

				let loadMore: boolean = false;

				if (displayMode === "showAll") {
					// append order headers, append view models
					orderHeaders.forEach((orderHeader: OrderHeader) => {
						const orderHeaderVM = this._orderHeaderConverterService.createOrderHeaderViewModel(orderHeader);
						this.orderHeadersVMs.push(orderHeaderVM);

						// CASS-563: Cancelled/Delited orders/plans should not be shown in Rialto UI
						const isNotCancelled = !orderHeaderVM.statusVM.cancelled

						if (this.isAllowedByFilter(orderHeaderVM) && isNotCancelled) {
							this.refinedOrderHeadersVMs.push(orderHeaderVM);
						}
					});

					loadMore = orderHeaders.length > 0 && pageOffset <= this.MAX_PAGES && orderHeaders.length === pageSize;
				}
				else if (displayMode === "showRecent") {
					// append only up to this.RECENT_ITEMS
					orderHeaders.forEach((orderHeader: OrderHeader) => {
						const orderHeaderVM = this._orderHeaderConverterService.createOrderHeaderViewModel(orderHeader);
						this.orderHeadersVMs.push(orderHeaderVM);

						// CASS-563: Cancelled/Delited orders/plans should not be shown in Rialto UI
						const isNotCancelled = !orderHeaderVM.statusVM.cancelled

						if (this.refinedOrderHeadersVMs.length < this.RECENT_ITEMS && this.isAllowedByFilter(orderHeaderVM) && isNotCancelled) {
							this.refinedOrderHeadersVMs.push(orderHeaderVM);
						}
					});
					// TODO why bother paging the "recent list"? And why is this a separate case? The page size should be passed into this function.
					loadMore = this.refinedOrderHeadersVMs.length + 1 < this.RECENT_ITEMS && orderHeaders.length === pageSize;
				} else if (displayMode === "externalPlanner") {
					// If advertiserId and agencyId queryParams are not defined, displayMode should be 'showAll'
					if (!this.advertiserId && !this.agencyId) {
						this._displayMode$.next('showAll');
					}
					orderHeaders.forEach((orderHeader: OrderHeader) => {
						const orderHeaderVM = this._orderHeaderConverterService.createOrderHeaderViewModel(orderHeader);
						this.orderHeadersVMs.push(orderHeaderVM);
						// Only Show Orders which are not associated with external planner & has status Draft or Reserved
						const isDraftOrReserved = orderHeaderVM.statusVM.draft || orderHeaderVM.statusVM.reservedAll || orderHeaderVM.statusVM.reservedAdCount > 0;
						if (!orderHeaderVM.orderHeader.externalPlanId && this.isAllowedByFilter(orderHeaderVM) && isDraftOrReserved) {
							this.refinedOrderHeadersVMs.push(orderHeaderVM);
						}
					});
					loadMore = orderHeaders.length > 0 && pageOffset <= this.MAX_PAGES && orderHeaders.length === pageSize;
				}
				else {
					this._logger.error(this.COMPONENT_NAME, `Bug in LoadOrderHeaders(): displayMode="${displayMode}" is not supported`);
				}

				if (loadMore) {
					this.loadOrderHeaders(startDate, advertiserId, otherPayerId, displayMode, profile, pageOffset, pageSize, polySearch);
				}
				else {
					this._ordersLoadingComplete$.next(true);
				}
			},
			error: (error: CassError) => {
				this.callingOut$.next(false);
				this._logger.error(this.COMPONENT_NAME, `Cannot get order headers by startDate=${startDate.toLocaleDateString()}, advertiserId=${advertiserId}, otherPayerId=${otherPayerId} for user=${profile?.email}`, error);
			}
		});

		this.subs.add(this.getOrderHeadersSubscribe);
	}

	cardClicked(idx: Number) {
		if (!this.forceExpand) {
			if (this.expandedItemIdx === idx) {
				this.expandedItemIdx = undefined;
			}
			else {
				this.expandedItemIdx = idx;
			}
		}
	}

	getAgencyContact(otherPayerId: string | undefined, agencyContactId: string | undefined): CustomerContact | undefined {
		return this._userProfilesService.getAgencyContact(otherPayerId, agencyContactId);
	}

	openOrder(orderId: string) {
		this._router.navigate(['/order'], { queryParams: { orderId: orderId } });
	}

	searchOrders(event: any): void {
		if (event) {
			// prevents the default form submit action and attempt to move the focus,
			// so the escape action is not triggered together with Enter
			event.preventDefault();
		}

		if (this.getOrderHeadersSubscribe) {
			this.getOrderHeadersSubscribe.unsubscribe();
			this.getOrderHeadersSubscribe = undefined;
		}

		this.updateOrderHeaders(this._userProfilesService.userProfile$.value, this.displayMode, this.advertiserId, this.agencyId);
	}

	resetSearch(): void {
		this.searchControl.setValue("");
		this.updateOrderHeaders(this._userProfilesService.userProfile$.value, this.displayMode, this.advertiserId, this.agencyId);
	}

	filterPanelShowHide(values: string[]): void {
		this.isFilterPanelActive = values && values.length > 0 && values.includes("filterPanel");
		this.updateRefinedOrderHeaderVMs();
	}

	productFilterValueChanged(event: MatSelectChange) {
		this.productFilterValue = event.value;
		this.updateRefinedOrderHeaderVMs();
	}

	contactIdFilterValueChanged(event: MatSelectChange) {
		this.contactIdFilterValue = event.value;
		this.updateRefinedOrderHeaderVMs();
	}

	statusFilterValueChanged(event: MatSelectChange) {
		this.statusFilterValue = event.value;
		this.updateRefinedOrderHeaderVMs();
	}

	issueDateFromValueChanged(event: MatDatepickerInputEvent<Date>) {
		this.issueDateFromFilterValue = event.value || undefined;
		this.issueDateFromFilterString = this.issueDateFromFilterValue ? this._dateTimeService.toBackendDate(this.issueDateFromFilterValue) : "";
		this.updateRefinedOrderHeaderVMs();
	}

	issueDateToValueChanged(event: MatDatepickerInputEvent<Date>) {
		this.issueDateToFilterValue = event.value || undefined;
		this.issueDateToFilterString = this.issueDateToFilterValue ? this._dateTimeService.toBackendDate(this.issueDateToFilterValue) : "";
		this.updateRefinedOrderHeaderVMs();
	}

	clearIssueDateFrom(): void {
		this.issueDateFromFilterValue = undefined;
		this.issueDateFromFilterString = "";
		this.updateRefinedOrderHeaderVMs();
	}

	clearIssueDateTo(): void {
		this.issueDateToFilterValue = undefined;
		this.issueDateToFilterString = "";
		this.updateRefinedOrderHeaderVMs();
	}

	private resetOrderHeaderVMs(): void {
		this.orderHeadersVMs = [];
		this.refinedOrderHeadersVMs = [];
	}

	private updateRefinedOrderHeaderVMs(): void {
		this.refinedOrderHeadersVMs = this.orderHeadersVMs.filter((orderHeaderVM: OrderHeaderViewModel) => this.isAllowedByFilter(orderHeaderVM));

		this.displayNoAdsAlert = this.refinedOrderHeadersVMs.length === 0;
	}

	private isAllowedByFilter(orderHeaderVM: OrderHeaderViewModel): boolean {

		if (!this.isFilterPanelActive) {
			return true;
		}

		if (this.productFilterValue && (!orderHeaderVM.orderHeader.packageLabels || !orderHeaderVM.orderHeader.packageLabels.includes(this.productFilterValue))) {
			return false;
		}

		if (this.issueDateFromFilterString && orderHeaderVM.orderHeader.endDate && this.issueDateFromFilterString > orderHeaderVM.orderHeader.endDate) {
			return false;
		}

		if (this.issueDateToFilterString && orderHeaderVM.orderHeader.startDate && this.issueDateToFilterString < orderHeaderVM.orderHeader.startDate) {
			return false;
		}

		if (this.contactIdFilterValue) {
			const contact = this.contacts.find((c: CustomerContactViewModel) => c.id === this.contactIdFilterValue);
			if (contact) {
				const foundByName = orderHeaderVM.orderHeader.contactFirstName === contact.firstName && orderHeaderVM.orderHeader.contactLastName === contact.lastName;
				const foundByAgencyId = orderHeaderVM.orderHeader.agencyContactId === contact.id;
				if (!foundByName && !foundByAgencyId) {
					return false;
				}
			}
		}

		if (this.statusFilterValue) {
			switch (this.statusFilterValue) {
				case "ORDER_BASKET.STATUS.DRAFT":
					if (!orderHeaderVM.statusVM.draft) return false; break;
				case "ORDER_BASKET.STATUS.STARTED":
					if (!orderHeaderVM.statusVM.started) return false; break;
				case "ORDER_BASKET.STATUS.FINISHED":
					if (!orderHeaderVM.statusVM.finished) return false; break;
				case "ORDER_BASKET.STATUS.APPROVAL_PENDING":
					if (orderHeaderVM.statusVM.approvalPendingAdCount <= 0) return false; break;
				case "ORDER_BASKET.STATUS.REJECTED":
					if (orderHeaderVM.statusVM.rejectedAdCount <= 0) return false; break;
				case "ORDER_BASKET.STATUS.RESERVED":
					if (orderHeaderVM.statusVM.reservedAdCount <= 0) return false; break;
				case "ORDER_BASKET.STATUS.CONFIRMED":
					if (orderHeaderVM.statusVM.confirmedAdCount <= 0) return false; break;
				case "ORDER_BASKET.STATUS.WAITING_MATERIAL":
					if (!orderHeaderVM.statusVM.waitingMaterial) return false; break;
				case "ORDER_BASKET.STATUS.CANCELLED":
					if (!orderHeaderVM.statusVM.cancelled) return false; break;
			}
		}

		// CASS-563: Cancelled/Delited orders/plans should not be shown in Rialto UI.
		// return false if statusFilterValue is undefined (All) and status is cancelled else return true.
		// This condition will filter the cancelled orders If user selects All status in filter panel.

		return !(!this.statusFilterValue && orderHeaderVM.statusVM.cancelled);
	}

	getOtherPayerName(otherPayerId: string | undefined) {
		let profile = this._userProfilesService.userProfile$.value
		if (otherPayerId != undefined && profile && profile.customers && profile.customers.length > 0) {
			let agency = profile.customers.find(x => x.id === otherPayerId);
			return agency?.name ?? '';
		}
		return undefined;
	}

	private updateCustomerAndAgencyVisibility(profile: UserProfile) {
		// Shown: profile has multiple customers, or a single customer with clients.
		// Hidden: profile has only one customer, and that customer has no clients.

		// profile has multiple customers
		if (profile && profile.customers && profile.customers.length > 1) {
			this.showCustomerAndAgency = true;
			return;
		}
		// profile has single customer
		if (profile && profile.customers && profile.customers?.length === 1) {
			this.subs.add(
				this._customerService.getCustomers(profile.customers[0].id ?? '').subscribe((res) => {
					// check if customer has one or more clients
					this.showCustomerAndAgency = res && res.length > 0;
				}));
		}
	}
}
